import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { getReviews, getRestaurantInfo } from '../services/api';

const DEFAULT_INFO = {
  rating: 4.4,
  totalReviews: 2444
};

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [info, setInfo] = useState(DEFAULT_INFO);

  useEffect(() => {
    const fetchData = async () => {
      const [reviewsData, infoData] = await Promise.all([
        getReviews(),
        getRestaurantInfo()
      ]);
      setReviews(Array.isArray(reviewsData) ? reviewsData : []);
      if (infoData) {
        setInfo({ ...DEFAULT_INFO, ...infoData });
      }
    };
    fetchData();
  }, []);

  const renderStars = (rating) => {
    return [0, 1, 2, 3, 4].map((i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  const safeReviews = Array.isArray(reviews) ? reviews : [];
  const rating = info.rating || 0;
  const totalReviews = info.totalReviews || 0;

  return (
    <section id="ulasan" className="py-24 bg-gradient-to-b from-white to-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
            Testimoni
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Apa Kata Pelanggan Kami
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Kepuasan pelanggan adalah prioritas utama kami
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-16">
          <div className="text-center">
            <div className="text-6xl font-bold text-gray-900 mb-2">
              {rating}
            </div>
            <div className="flex items-center justify-center gap-1 mb-2">
              {renderStars(Math.round(rating))}
            </div>
            <p className="text-gray-600">
              Dari {totalReviews.toLocaleString()} ulasan
            </p>
          </div>

          <div className="hidden md:block w-px h-24 bg-gray-200" />

          <div className="space-y-2 w-64">
            {[5, 4, 3, 2, 1].map((star) => {
              const percentage = star === 5 ? 65 : star === 4 ? 25 : star === 3 ? 7 : star === 2 ? 2 : 1;
              return (
                <div key={star} className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 w-8">{star}</span>
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-10">{percentage}%</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {safeReviews.map((review) => (
            <Card
              key={review.id}
              className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white"
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {review.avatar}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900">{review.name}</h4>
                    <div className="flex items-center gap-2">
                      <div className="flex">{renderStars(review.rating || 0)}</div>
                      <span className="text-sm text-gray-500">{review.date}</span>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <Quote className="absolute -top-2 -left-2 w-8 h-8 text-amber-100" />
                  <p className="text-gray-600 leading-relaxed pl-4">
                    {review.comment}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            Ulasan dikumpulkan dari Google Maps & pelanggan langsung
          </p>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
